import React, { useEffect, useState } from 'react';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { useEffectAsync } from '../../../../helpers';
import * as yup from 'yup';
import { FieldError } from 'react-hook-form';
import useNCZICommandProcess from '../../Hooks/useNCZICommandProcess';
import { SearchMedicaments } from '../../Commands/SearchMedicaments';
import { useNCZIContext } from '../../Services/NCZIContext';
import useTranslation from '../../../../Hooks/useTranslation';
import { useValidation } from '../../../../Components/Utils/Validation';
import AsyncSelect from '../../../../Components/Base/Select/AsyncSelect';
import Text from '../../../../Components/Base/Text/Text';

// @ts-ignore
import styles from '../../../../Components/Base/DynamicSelect/DynamicSelect.module.scss';

export interface NCZIInsuranceMedicament {
	code: string;
	name: string;
	additionalName: string;
	atcCode: string;
	atcName: string;
}

export interface NCZIInsuranceMedicamentSelectItem {
	label: string;
	value: NCZIInsuranceMedicament;
}

export const NCZIInsuranceMedicamentValidator = (name: string) =>
	yup.object({
		label: yup.string(),
		value: yup.object({
			code: yup.string().required(name),
			name: yup.string(),
			additionalName: yup.string(),
			atcCode: yup.string(),
			atcName: yup.string(),
		}),
	});

interface Props {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue;
	error?: FieldError;
}

export const NCZIInsuranceMedicamentSearchSelect: React.FC<Props> = props => {
	const { value, onChange, error } = props;
	const [resultCallback, setResultCallback] = useState<
		((items) => void) | undefined
	>(undefined);
	const { patientInsurance } = useNCZIContext();
	const { t } = useTranslation();
	const { fillErrorMessage } = useValidation();
	const [searchTimeout, setSearchTimeout] = useState<
		NodeJS.Timeout | undefined
	>(undefined);
	const { processCommand } = useNCZICommandProcess({
		onFinish: (id, result) => processResult(result?.medicaments ?? []),
		hideSuccess: true,
		hideError: true,
	});

	useEffectAsync(async () => {
		if (value) onChange?.(value);
	});

	const processResult = (items: NCZIInsuranceMedicament[]) => {
		const selectItems = items.map((item: NCZIInsuranceMedicament) => {
			return {
				label:
					item.code +
					': ' +
					item.name +
					(item.additionalName ? ' (' + item.additionalName + ')' : ''),
				value: item,
			};
		});

		resultCallback?.(selectItems);
	};

	const loadOptions = (
		inputValue: string,
		callback: (options: SelectValue[]) => void,
	) => {
		setResultCallback(prevState => callback);
		if (!inputValue) {
			return callback([]);
		}

		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		// @ts-ignore
		const wCall = async () => {
			const searchCommand = SearchMedicaments({
				insurance: patientInsurance!,
				medicamentCode: [inputValue],
			});

			await processCommand(searchCommand);
		};

		setSearchTimeout(setTimeout(wCall, 500));
	};

	return (
		<div style={{ flex: 1 }}>
			<AsyncSelect
				value={value}
				onSearch={loadOptions}
				onChange={newValue => {
					if (newValue) {
						onChange?.(newValue);
					}
				}}
				placeholder={(value && value.label) || t('base.shared.pick')}
				noOptionsMessage={t('base.startTyping')}
				error={error}
			/>
			{error && (
				<div className={styles.errorMessage}>
					<Text tx={fillErrorMessage(error)} inline={true} />
				</div>
			)}
		</div>
	);
};

export default NCZIInsuranceMedicamentSearchSelect;
